/**
 * Custom Video Player Module (mobile)
 * @module customVideoPlayer
 */

import $ from 'jquery';
import registerJQueryPlugin from '../../lib/register-jquery-plugin';
import mergeOptions from '../../lib/merge-options';

export const PLUGIN_NAME = 'customVideoPlayer';
export const DEFAULTS = {
  videoMuted: true,
  hideMute: false,
  $originalTrigger: $(),
  titleClass: 'floating-video__title',
  captionClass: 'floating-video__caption',
};

function formatTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
}

function enterFullScreen(playerContainer) {
  if (playerContainer.requestFullscreen) {
    playerContainer.requestFullscreen();
  } else if (playerContainer.mozRequestFullScreen) {
    playerContainer.mozRequestFullScreen();
  } else if (playerContainer.webkitRequestFullscreen) {
    playerContainer.webkitRequestFullscreen();
  } else if (playerContainer.msRequestFullscreen) {
    playerContainer.msRequestFullscreen();
  }
}

function isVideoPlaying(videoElement) {
  return !videoElement.paused && !videoElement.ended;
}

const createVideoWrapper = () => $('<div class="custom-video__container"></div>');

const createVideoControls = ({ hideMute }, title, caption) => `
    <div class="js-floating-video-controls custom-video__controls">
      <div class="js-custom-video__play custom-video__play">
        <span class="icon icon--play" style="width:84px;height:84px;display: none">
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 38 38">
            <circle cx="19" cy="19" r="8.5" fill="#fff" stroke="#f5f5f5" />
            <path d="M16,22c0,.4.5.7.9.5l6.1-3c.4-.2.4-.9,0-1.1l-6.1-3c-.4-.2-.9,0-.9.5v6.1Z" fill="#3b3b3b" />
          </svg>
        </span>
        <span class="icon icon--pause" style="width:40px;height:40px;">
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 32 32">
            <path fill="#f5f5f5" d="M5.5,28h6.9V4h-6.9v24ZM19.2,4v24h6.9V4h-6.9Z" />
          </svg>
        </span>
      </div>
      <div class="custom-video__bottom-controls">
        <div class="custom-video__bottom-controls-container">
          ${(title && caption) ? `<div class="js-custom-video__caption">
              <div class="u-flex" style="justify-content: space-between;">
                <div><strong class="u-text--md">${title}</strong></div>
                <button class="custom-video__caption-button">
                  <span class="icon icon--arrow-up u-color--white">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                      <path d="M11.25 10.5a.748.748 0 0 1-.55-.24L8 7.352 5.3 10.26a.75.75 0 0 1-1.1-1.02l3.25-3.5a.774.774 0 0 1 1.1 0l3.25 3.5a.75.75 0 0 1-.55 1.26z"/>
                    </svg>
                  </span>
                </button>
              </div>
              <p>${caption}</p>
          </div>` : ''}
          <div class="custom-video__bottom-controls-wrap">
            <div class="js-custom-video__current-time-container custom-video__current-time-container">
                <span class="js-custom-video__current-time custom-video__current-time">0:00</span> / <span
                  class="js-custom-video__total-time">0:00</span>
            </div>
            <div class="u-flex" style="align-items: center;">
              <div style="flex-grow:1;">
                <div class="js-custom-video__progress-bar-container custom-video__progress-bar-container">
                  <div class="js-custom-video__progress-bar custom-video__progress-bar">
                    <div class="js-custom-video__progress custom-video__progress"></div>
                    <div class="js-custom-video__thumb custom-video__thumb"></div>
                  </div>
                </div>
              </div>
              <div class="u-flex">
                <div class="js-custom-video__fullscreen custom-video__fullscreen">
                  <span class="icon icon--fullscreen icon--md">
                    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 24 24">
                      <path
                        d="M6,19c-.3,0-.5,0-.7-.3-.2-.2-.3-.4-.3-.7v-3c0-.3,0-.5.3-.7.2-.2.4-.3.7-.3s.5,0,.7.3c.2.2.3.4.3.7v2h2c.3,0,.5,0,.7.3.2.2.3.4.3.7s0,.5-.3.7c-.2.2-.4.3-.7.3h-3ZM6,10c-.3,0-.5,0-.7-.3-.2-.2-.3-.4-.3-.7v-3c0-.3,0-.5.3-.7.2-.2.4-.3.7-.3h3c.3,0,.5,0,.7.3.2.2.3.4.3.7s0,.5-.3.7c-.2.2-.4.3-.7.3h-2v2c0,.3,0,.5-.3.7-.2.2-.4.3-.7.3ZM15,19c-.3,0-.5,0-.7-.3-.2-.2-.3-.4-.3-.7s0-.5.3-.7c.2-.2.4-.3.7-.3h2v-2c0-.3,0-.5.3-.7.2-.2.4-.3.7-.3s.5,0,.7.3c.2.2.3.4.3.7v3c0,.3,0,.5-.3.7-.2.2-.4.3-.7.3h-3ZM18,10c-.3,0-.5,0-.7-.3-.2-.2-.3-.4-.3-.7v-2h-2c-.3,0-.5,0-.7-.3-.2-.2-.3-.4-.3-.7s0-.5.3-.7c.2-.2.4-.3.7-.3h3c.3,0,.5,0,.7.3.2.2.3.4.3.7v3c0,.3,0,.5-.3.7-.2.2-.4.3-.7.3Z" />
                    </svg>
                  </span>
                </div>
                ${hideMute ?
                  `<div class="js-custom-video__mute">
                    <span class="icon icon--unmute icon--md">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path
                          d="M14.2,6.4c.2-.3.6-.5.9-.3,2.3,1.1,3.9,3.3,3.9,5.9s-1.6,4.8-3.9,5.9c-.3.2-.8,0-.9-.3-.2-.3,0-.7.3-.9,1.8-.9,3.1-2.7,3.1-4.7s-1.3-3.9-3.1-4.7c-.3-.2-.5-.6-.3-.9Z" />
                        <path
                          d="M7.8,9.3h-1.4c-.8,0-1.4.6-1.4,1.3v2.6c0,.7.6,1.3,1.4,1.3h1.4l3.1,2.4c.5.4,1.1,0,1.1-.5V7.5c0-.6-.7-.9-1.1-.5l-3.1,2.4Z" />
                        <path
                          d="M15.4,9.6c-.2-.3-.7-.4-1-.1-.3.2-.4.6-.1.9.3.4.6,1,.6,1.6s-.2,1.1-.6,1.6c-.2.3-.2.7.1.9.3.2.7.2,1-.1.5-.7.8-1.5.8-2.4s-.3-1.7-.8-2.4Z" />
                      </svg>
                    </span>
                    <span class="icon icon--mute icon--md" style="display: none">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path
                          d="M17.6128 18.7968L15.9153 17.0792C15.7449 17.1777 15.5745 17.273 15.4042 17.3651C15.2338 17.4577 15.0574 17.5409 14.8748 17.6148C14.6315 17.7133 14.4063 17.6825 14.1995 17.5224C13.9926 17.3624 13.8892 17.1407 13.8892 16.8575C13.8892 16.7221 13.9288 16.6021 14.0082 16.4977C14.087 16.3928 14.1873 16.3157 14.309 16.2665C14.4063 16.2296 14.5008 16.1894 14.5923 16.1461C14.6833 16.1032 14.7714 16.0572 14.8566 16.0079L12.4289 13.5515V15.6016C12.4289 15.934 12.2798 16.1648 11.9814 16.2938C11.6835 16.4234 11.4189 16.3712 11.1877 16.1372L8.77836 13.6992H6.58801C6.38114 13.6992 6.20786 13.6283 6.06816 13.4864C5.92798 13.3451 5.85789 13.1697 5.85789 12.9604V10.0053C5.85789 9.79595 5.92798 9.62037 6.06816 9.47852C6.20786 9.33717 6.38114 9.26649 6.58801 9.26649H8.19426L5.20078 6.23747C5.06693 6.10202 5 5.92964 5 5.72032C5 5.51099 5.06693 5.33861 5.20078 5.20317C5.33464 5.06772 5.505 5 5.71186 5C5.91873 5 6.08909 5.06772 6.22295 5.20317L18.6349 17.7625C18.7688 17.898 18.8357 18.0704 18.8357 18.2797C18.8357 18.489 18.7688 18.6614 18.6349 18.7968C18.5011 18.9323 18.3307 19 18.1239 19C17.917 19 17.7466 18.9323 17.6128 18.7968ZM14.8748 5.31398C16.116 5.81882 17.1139 6.62533 17.8683 7.73351C18.6228 8.84169 19 10.0853 19 11.4644C19 12.117 18.9119 12.7449 18.7357 13.3483C18.559 13.9516 18.3064 14.5119 17.9778 15.029L16.9192 13.9578C17.126 13.5761 17.2813 13.1759 17.385 12.7573C17.4882 12.3386 17.5398 11.9077 17.5398 11.4644C17.5398 10.3808 17.2448 9.40809 16.6549 8.54617C16.0644 7.68426 15.2825 7.05629 14.309 6.66227C14.1751 6.61302 14.0717 6.53298 13.9987 6.42216C13.9257 6.31135 13.8892 6.18821 13.8892 6.05277C13.8892 5.78188 13.9926 5.5664 14.1995 5.40633C14.4063 5.24626 14.6315 5.21548 14.8748 5.31398ZM15.5319 12.5541L13.8892 10.8918V8.49077C14.4611 8.76165 14.9084 9.16799 15.2311 9.70976C15.5534 10.2515 15.7145 10.8426 15.7145 11.4829C15.7145 11.6675 15.6994 11.8493 15.6692 12.0281C15.6385 12.2064 15.5928 12.3817 15.5319 12.5541ZM12.4289 9.41425L10.5306 7.4934L11.1877 6.8285C11.4189 6.59455 11.6835 6.54209 11.9814 6.67113C12.2798 6.80067 12.4289 7.03166 12.4289 7.36412V9.41425Z" />
                      </svg>
                    </span>
                  </div>` : ''
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
`;

const applyVideoSkin = ($video, options) => {
  const videoWrapper = createVideoWrapper();
  $video.wrap(videoWrapper);
  const $videoWrapper = $video.parent();
  const $titleHidden = options.$originalTrigger.find(`.${options.titleClass}`);
  const $captionHidden = options.$originalTrigger.find(`.${options.captionClass}`);
  const controlsHtml = createVideoControls(options, $titleHidden.text(), $captionHidden.text());
  const $videoControls = $(controlsHtml);
  $videoWrapper.prepend($videoControls);

  const $playPauseButton = $videoWrapper.find('.js-custom-video__play');
  const $muteButton = $videoWrapper.find('.js-custom-video__mute');
  const $captionButton = $videoWrapper.find('.js-custom-video__caption');

  const $currentTimeContainer = $videoWrapper.find('.js-custom-video__current-time-container');
  const $currentTimeDisplay = $videoWrapper.find('.js-custom-video__current-time');
  const $totalTimeDisplay = $videoWrapper.find('.js-custom-video__total-time');
  const $progressBarContainer = $videoWrapper.find('.js-custom-video__progress-bar-container');
  const $progressBar = $videoWrapper.find('.js-custom-video__progress-bar');
  const $progress = $videoWrapper.find('.js-custom-video__progress');
  const $thumb = $videoWrapper.find('.js-custom-video__thumb');
  const $fullscreenIcon = $videoWrapper.find('.js-custom-video__fullscreen');

  const $muteIcon = $videoWrapper.find('.icon--mute');
  const $unmuteIcon = $videoWrapper.find('.icon--unmute');
  const $playIcon = $videoWrapper.find('.icon--play');
  const $pauseIcon = $videoWrapper.find('.icon--pause');

  const video = $video[0];
  const { videoMuted } = options;

  let hideControlsTimer;

  const showPlayerControls = () => {
    $videoWrapper.addClass('custom-video__container--controls-visible');
  };

  const scheduleHidePlayerControls = () => {
    clearTimeout(hideControlsTimer);
    hideControlsTimer = window.setTimeout(() => {
      $videoWrapper.removeClass('custom-video__container--controls-visible');
    }, 2500);
  };

  if (!videoMuted) {
    switchMuteIcon();
  }

  $captionButton.on('click', () => {
    $captionButton.toggleClass('expanded');
    $videoWrapper.toggleClass('custom-video__controls-caption--expanded');
  });

  const playPromise = video.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {});
  }
  showPlayerControls();
  video.volume = 1;
  video.removeAttribute('controls');

  $playPauseButton.on('click', () => {
    if (isVideoPlaying(video)) {
      video.pause();
    } else {
      video.play();
    }
  });

  $videoWrapper.on('click.controlsVisibility', () => {
    showPlayerControls();
    if (isVideoPlaying(video)) {
      scheduleHidePlayerControls();
    }
  });

  $video.on('play', () => {
    $playIcon.hide();
    $pauseIcon.show();
    scheduleHidePlayerControls();
  });

  $video.on('pause', () => {
    $playIcon.show();
    $pauseIcon.hide();
    clearTimeout(hideControlsTimer);
    showPlayerControls();
  });

  video.onloadedmetadata = () => {
    $totalTimeDisplay.text(formatTime(video.duration));
  };

  video.ontimeupdate = () => {
    $currentTimeDisplay.text(formatTime(video.currentTime));
    updateProgressBar();
  };

  $progressBarContainer.on('mousedown.customVideoPlayer touchstart.customVideoPlayer', (e) => {
    updateCurrentTime(e.type === 'touchstart' ? e.originalEvent.touches[0] : e);

    $(document).on('mousemove.customVideoPlayer touchmove.customVideoPlayer', (ev) => {
      updateCurrentTime(ev.type === 'touchmove' ? ev.originalEvent.touches[0] : ev);
    });
    $(document).on('mouseup.customVideoPlayer touchend.customVideoPlayer', () => {
      $(document).off('mousemove.customVideoPlayer touchmove.customVideoPlayer');
      $(document).off('mouseup.customVideoPlayer touchend.customVideoPlayer');
      window.setTimeout(() => $currentTimeContainer.removeClass('show'), 1000);
    });
  });

  $progressBarContainer.on('mouseenter', () => {
    $currentTimeContainer.addClass('show');
  });

  $progressBarContainer.on('mouseleave', () => {
    $currentTimeContainer.removeClass('show');
  });

  function updateCurrentTime(e) {
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    const offsetX = (e.pageX !== undefined ? e.pageX : e.clientX) - $progressBar.offset().left;
    const width = $progressBar.width();
    const clickPosition = Math.max(0, Math.min(offsetX / width, 1));
    video.currentTime = clickPosition * video.duration;
    updateProgressBar();
    $currentTimeDisplay.text(formatTime(video.currentTime));
  }

  function updateProgressBar() {
    const progressPercentage = (video.currentTime / video.duration) * 100;
    $progress.css('width', `${progressPercentage}%`);
    const thumbPosition = (progressPercentage / 100) * $progressBar.width();
    $thumb.css('left', `${thumbPosition}px`);
  }

  $muteButton.on('click', () => {
    switchMuteIcon();
  });

  function switchMuteIcon() {
    video.muted = !video.muted;
    if (video.muted) {
      $muteIcon.show();
      $unmuteIcon.hide();
    } else {
      $muteIcon.hide();
      $unmuteIcon.show();
    }
  }

  switchMuteIcon();

  $fullscreenIcon.on('click', () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      enterFullScreen($videoWrapper[0]);
      $videoWrapper.addClass('is-fullscreen');
    }
  });

  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      $videoWrapper.removeClass('is-fullscreen');
    }
  });
};

export default function customVideoPlayer(el, options = {}) {
  const $video = $(el);
  const opts = mergeOptions(DEFAULTS, options, $video);
  applyVideoSkin($video, opts);
  return {};
}
registerJQueryPlugin(PLUGIN_NAME, customVideoPlayer);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/abtest/custom-video-player.js